import { BusinessActs } from "../../../components";
import { dateToHumanFormat, dateToHumanFormatShortWithTime } from "../../../utils/constants";
import useConfigOptions from "../../../hooks/useConfigOptions";
import { workYearsReverse } from "../../../utils/loanConstants";

const SectionHeader = ({ label }) => (
    <tr>
        <td colSpan={2} className="px-5 py-2.5 bg-gray-50/80 dark:bg-gray-900/40 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide border-y border-gray-100 dark:border-gray-700">
            {label}
        </td>
    </tr>
);

const InfoRow = ({ label, value, bold }) => (
    <tr className="hover:bg-gray-50/40 dark:hover:bg-gray-700/30 transition-colors">
        <td className="px-5 py-3.5 text-sm text-gray-500 dark:text-gray-400 w-2/5">{label}</td>
        <td className={`px-5 py-3.5 text-sm ${bold ? 'font-semibold text-gray-900 dark:text-gray-100' : 'font-medium text-gray-800 dark:text-gray-200'}`}>{value}</td>
    </tr>
);

function Info({ loan }) {

    const { loanTypes } = useConfigOptions();
    return (
        <>
        <SectionHeader label="Loan Application" />
        <InfoRow label="Loan application origination" value={loan?.client?.tag === "staff" ? "Staff portal" : "Client portal"} />
        <InfoRow label="Account created on" value={dateToHumanFormatShortWithTime(loan?.client?.createdAt)} />

        {loan?.reference && <InfoRow label="Reference code for momo repayments" value={loan?.reference} />}
        {loan?.mobileMoneyAccountName && <InfoRow label="Name on mobile money account" value={loan?.mobileMoneyAccountName} />}
        {loan?.isRollover && (
            <InfoRow
                label="This loan is a rollover"
                value={<span>with previous balance of GHC <strong>{loan?.rolloverLoan?.balance}</strong></span>}
            />
        )}

        <SectionHeader label="Personal / Business Information" />

        {loan?.client?.failedVerification && (
            <tr>
                <td colSpan={2} className="px-5 py-3 bg-red-50 dark:bg-red-500/10 text-sm text-red-700 dark:text-red-400">
                    Failed NIA ID Verification — please verify the client&apos;s information
                </td>
            </tr>
        )}

        <InfoRow label="Name" value={`${loan?.client?.otherNames} ${loan?.client?.surname}`} />
        <InfoRow label="Phone" value={loan?.client?.phone} />
        <InfoRow label="Email Address" value={loan?.client?.email} />
        <InfoRow label="Digital Address" value={loan?.client?.digitalAddress} />
        <InfoRow label="Residential Address" value={loan?.client?.address} />
        <InfoRow label="Signed Name on Loan" value={`${loan?.client?.otherNames} ${loan?.client?.surname}`} />
        <InfoRow label="Employer's Name" value={loan?.loanKind === "AJ & A Services" ? "AJ & A Services (Contract Staff)" : loan?.employerName} />
        <InfoRow label="Employment Status" value={loan?.employedCurrently} />
        <InfoRow label="Number of years at work" value={workYearsReverse[loan?.yearsAtWork]} />

        {(loan?.loanType === loanTypes?.capitalme || loan?.loanType === loanTypes?.cashme) &&
            <BusinessActs business={loan?.business[0]} loan={loan} />
        }

        <SectionHeader label="Credit Payment Information" />
        <InfoRow label="Credit Score" value={`${loan?.score}%`} bold />
        <InfoRow label="Debt to Income Ratio" value={`${loan?.DIR}%`} />
        <InfoRow label="Willingness conclusion" value={loan?.willingness} />
        <InfoRow label="Capacity conclusion" value={loan?.capacity} />
        <InfoRow label="Preliminary credit conclusion" value={loan?.creditConc} />

        <SectionHeader label="Income Information Details" />
        <InfoRow label="Income or Revenue" value={`GHC ${loan?.income}`} />
        <InfoRow label="Frequency of Income" value={loan?.freqIncome} />
        <InfoRow label="Debt Payments" value={loan?.debtAmt} />
        <InfoRow label="Savings Habit" value={
            loan?.savingHabits === "Yes"
                ? `${loan?.savingHabits} — [M1 - ${loan?.savingsFreq1}] — [M2 - ${loan?.savingsFreq2}] — [M3 - ${loan?.savingsFreq3}]`
                : loan?.savingHabits
        } />
        <InfoRow label="Insurance Habit / Type" value={`${loan?.typeOfInsurance !== "None" ? "Yes" : "None"} / ${loan?.typeOfInsurance}`} />
        <InfoRow label="Are your loan payments automated" value={loan?.automatedPaymentsStatus} />
        <InfoRow label="System you currently use to automate your loan payments" value={loan?.autoDeduction} />
        <InfoRow label="Recent 3 months bills payment" value={loan?.billPaymentHistory} />
        <InfoRow label="Recent 3 months loan payment" value={loan?.loanPaymentHistory} />

        <SectionHeader label="Loan Details Information" />
        <InfoRow label="Loan Amount" value={loan?.amount} />
        <InfoRow label="Amount Requested" value={loan?.amountReq} />
        <InfoRow label="Eligible Amount" value={loan?.amountElig} bold />
        <InfoRow label="Number of Months" value={loan?.paymentDuration} />
        <InfoRow label="Payment Plan" value={loan?.paymentPlan} />
        <InfoRow label="Repayment Method" value={loan?.repaySource} />
        <InfoRow label="Taken a loan from other financial institution" value={loan?.loansTaken} />

        {(loan.loanType === loanTypes.cashme || loan.loanType === loanTypes.capitalme) && (
            <>
            <SectionHeader label="Mobile Money" />
            <InfoRow label="How long one has held their mobile money account" value={loan?.momoNumberDuration} />
            </>
        )}

        <SectionHeader label="Guarantor's Information" />
        <InfoRow label="Guarantor's Name" value={`${loan?.guarantorFirstName} ${loan?.guarantorLastName}`} />
        <InfoRow label="Guarantor's Phone" value={loan?.guarantorPhone} />
        <InfoRow label="Guarantor's ID Type and Number" value={`${loan?.guarantorIdType} : ${loan?.guarantorIdNumber}`} />
        <InfoRow label="Relationship with Guarantor" value={loan?.guarantorRelationship} />
        <InfoRow label="Guarantor's Address" value={loan?.guarantorAddress} />
        <InfoRow label="Guarantor Digital Address" value={loan?.guarantorDigitalAddress} />
        <InfoRow label="Guarantor's Date of Birth" value={dateToHumanFormat(loan?.guarantorDob)} />

        <SectionHeader label="Next of Kin Information" />
        <InfoRow label="Next of Kin" value={loan?.client?.nextOfKin ?? loan?.nextOfKin} />
        <InfoRow label="Next of Kin Phone Number" value={loan?.client?.nextOfKinPhone ?? loan?.nextOfKinPhone} bold />
        <InfoRow label="Address of Next of Kin" value={loan?.client?.nextOfKinAddress ?? loan?.nextOfKinAddress} />
        <InfoRow label="Relationship to Next of Kin" value={loan?.client?.nextOfKinRelationship ?? loan?.nextOfKinRelationship} />

        <SectionHeader label="Other Information" />
        <InfoRow label="Loan Relationship Manager" value={loan?.relManager} />
        </>
    );
}

export default Info;
